import { ParentTopic } from '../services/domain-topics';

export interface TopicPickerOption {
  slug: string;
  label: string;
  authorityScore: number;
  topicType: 'parent' | 'child';
  parentSlug: string;
  parentLabel: string;
  /** Humanized labels for merged inflection variants (e.g. singular/plural) */
  aliasLabels?: string[];
}

export function humanizeTopicSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.toLowerCase())
    .join(' ');
}

function toConservativeSingular(token: string): string {
  if (token.length <= 3) return token;
  if (token.endsWith('ies') && token.length > 4) return `${token.slice(0, -3)}y`;
  if (/(ses|xes|zes|ches|shes|oes)$/.test(token) && token.length > 4) return token.slice(0, -2);
  if (token.endsWith('s') && !/(ss|us|is)$/.test(token)) return token.slice(0, -1);
  return token;
}

function toConservativeVerbStem(token: string): string {
  if (token.length <= 5) return token;
  if (token.endsWith('ing') && token.length > 6) return token.slice(0, -3);
  if (token.endsWith('ed') && token.length > 5) return token.slice(0, -2);
  return token;
}

export function isInflectionEquivalentSlug(a: string, b: string): boolean {
  if (a === b) return true;
  const tokensA = a.split('-').filter(Boolean);
  const tokensB = b.split('-').filter(Boolean);
  if (tokensA.length === 0 || tokensB.length === 0 || tokensA.length !== tokensB.length) return false;
  if (toConservativeSingular(tokensA[0]) !== toConservativeSingular(tokensB[0])) return false;
  let diffIdx = -1;
  for (let i = 0; i < tokensA.length; i++) {
    if (tokensA[i] !== tokensB[i]) {
      if (diffIdx !== -1) return false;
      diffIdx = i;
    }
  }
  if (diffIdx === -1) return true;
  const singularA = toConservativeSingular(tokensA[diffIdx]);
  const singularB = toConservativeSingular(tokensB[diffIdx]);
  if (singularA === singularB) return true;
  if (diffIdx === tokensA.length - 1) {
    const stemA = toConservativeVerbStem(singularA);
    const stemB = toConservativeVerbStem(singularB);
    if (stemA === stemB && stemA.length >= 4) return true;
  }
  return false;
}

/** Same rules as backend inventory inflection filter (singular/plural + minor tail inflection). */
function pickPrimaryChildSlug(slugs: string[], counts: Record<string, number>): string {
  const norm = (s: string) => s.trim().toLowerCase();
  return slugs
    .map(norm)
    .filter(Boolean)
    .sort((a, b) => {
      const ca = counts[a] ?? 0;
      const cb = counts[b] ?? 0;
      if (cb !== ca) return cb - ca;
      if (a.length !== b.length) return a.length - b.length;
      return a.localeCompare(b);
    })[0];
}

function clusterChildSlugsByInflection(rawSlugs: string[]): string[][] {
  const list = [...new Set(rawSlugs.map((s) => s.trim().toLowerCase()).filter(Boolean))];
  const visited = new Set<string>();
  const clusters: string[][] = [];
  for (const start of list) {
    if (visited.has(start)) continue;
    const comp: string[] = [];
    const stack = [start];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      comp.push(cur);
      for (const other of list) {
        if (!visited.has(other) && isInflectionEquivalentSlug(cur, other)) {
          stack.push(other);
        }
      }
    }
    clusters.push(comp);
  }
  return clusters;
}

/** When `includeChildren` is false, only parent topics are listed. */
export function buildTopicOptionsFromParents(
  parents: ParentTopic[],
  includeChildren: boolean
): TopicPickerOption[] {
  const options: TopicPickerOption[] = [];
  const seenSlugs = new Set<string>();

  for (const parent of parents) {
    const parentSlug = parent.parentTopicSlug.trim().toLowerCase();
    if (!parentSlug || seenSlugs.has(parentSlug)) continue;
    seenSlugs.add(parentSlug);
    options.push({
      slug: parentSlug,
      label: parent.label,
      authorityScore: parent.authorityScore,
      topicType: 'parent',
      parentSlug,
      parentLabel: parent.label
    });

    if (!includeChildren) continue;
    const childTopicSlugs = Array.isArray(parent.childTopicSlugs) ? parent.childTopicSlugs : [];
    const counts = parent.childTopicPageCounts || {};
    const clusters = clusterChildSlugsByInflection(childTopicSlugs);

    for (const cluster of clusters) {
      if (cluster.length === 0) continue;
      const primarySlug = pickPrimaryChildSlug(cluster, counts);
      if (!primarySlug || primarySlug === parentSlug || seenSlugs.has(primarySlug)) continue;
      seenSlugs.add(primarySlug);
      const aliasSlugs = cluster.filter((s) => s !== primarySlug).sort((a, b) => a.localeCompare(b));
      const aliasLabels = aliasSlugs.map((s) => humanizeTopicSlug(s));
      options.push({
        slug: primarySlug,
        label: humanizeTopicSlug(primarySlug),
        authorityScore: parent.authorityScore,
        topicType: 'child',
        parentSlug,
        parentLabel: parent.label,
        ...(aliasLabels.length > 0 ? { aliasLabels } : {})
      });
    }
  }

  return options;
}

export function topicKindTagClass(topic: TopicPickerOption): string {
  return topic.topicType === 'child' ? 'topic-kind-child' : 'topic-kind-parent';
}
